<template>
  <div class="outstanding-summary-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Outstanding Summary Report</h4>
        <b-badge
          v-show="!loading && reportItems.length"
          variant="light-success"
          class="ml-2"
        >
          {{ reportItems.length }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" type="button" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" type="button" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading outstanding summary...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Header Section -->
      <div class="report-header-modern mb-3">
        <div class="header-inner position-relative text-center">
          <!-- Logo if available -->
          <div v-if="schoolLogo" class="logo-wrapper position-absolute">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>

          <div class="header-text-block">
            <h2 class="school-name-full mb-1">
              {{ campusName }}
            </h2>
            <div class="report-title-text">
              Class &amp; Section-Wise Outstanding, Advanced Fee, Fund &amp; Total Received Summary - {{ monthNameDisplay }} {{ year }}
            </div>
            <div class="generated-date-text mt-50" v-if="currentDateTime">
              <small>Generated on {{ currentDateTime }}</small>
            </div>
          </div>
        </div>
        <div class="header-divider-line mt-2"></div>
      </div>

      <!-- Grand Total Summary Card Bar matching reference design -->
      <div class="summary-metric-bar mb-3" v-if="grandTotal">
        <div class="summary-col">
          <div class="summary-label">TOTAL STRENGTH</div>
          <div class="summary-value">
            {{ formatCount(grandTotal.stdCount) }}
          </div>
        </div>

        <div class="summary-col">
          <div class="summary-label">PAID STRENGTH</div>
          <div class="summary-value">
            {{ formatCount(grandTotal.paidCount) }}
          </div>
        </div>

        <div class="summary-col">
          <div class="summary-label">FREE STRENGTH</div>
          <div class="summary-value">
            {{ formatCount(grandTotal.freeCount) }}
          </div>
        </div>

        <div class="summary-col">
          <div class="summary-label">UNPAID STRENGTH</div>
          <div class="summary-value">
            {{ formatCount(grandTotal.unpaidCount) }}
          </div>
        </div>

        <div class="summary-col">
          <div class="summary-label">OVERALL PAID %</div>
          <div class="summary-value">
            {{ formatPercent(grandTotal.paidPerc) }}
          </div>
        </div>

        <div class="summary-col">
          <div class="summary-label">TOTAL RECEIVED ({{ monthShortName }})</div>
          <div class="summary-value">
            PKR {{ formatAmount(grandTotal.totalReceived) }}
          </div>
        </div>

        <div class="summary-col">
          <div class="summary-label">FUND TOTAL ({{ monthShortName }})</div>
          <div class="summary-value">
            PKR {{ formatAmount(grandTotal.fundTotal) }}
          </div>
        </div>
      </div>

      <!-- Details Table -->
      <div class="table-container mt-3" v-if="reportItems && reportItems.length > 0">
        <table class="summary-data-table">
          <thead>
            <tr>
              <th class="text-left cell-class-sec">Class Sec</th>
              <th class="text-center cell-num">Total</th>
              <th class="text-center cell-num">Paid</th>
              <th class="text-center cell-num">Free</th>
              <th class="text-center cell-num">Unpaid</th>
              <th class="text-center cell-num">Paid %</th>
              <th class="text-center cell-amount cell-stacked-hdr">
                <div class="hdr-line">{{ currentMonthHdr }}</div>
                <div class="hdr-line">FEES</div>
              </th>
              <th class="text-center cell-num cell-stacked-hdr">
                <div class="hdr-line">{{ currentMonthHdr }} ADV</div>
                <div class="hdr-line">QTY</div>
              </th>
              <th class="text-center cell-amount cell-stacked-hdr">
                <div class="hdr-line">{{ currentMonthHdr }} ADV</div>
                <div class="hdr-line">AMT</div>
              </th>
              <!-- Dynamic Monthly Breakdown Headers -->
              <template v-for="col in distinctBreakdownColumns">
                <th class="text-center cell-num cell-stacked-hdr">
                  <div class="hdr-line">{{ col.headerTop }}</div>
                  <div class="hdr-line">QTY</div>
                </th>
                <th class="text-center cell-amount cell-stacked-hdr">
                  <div class="hdr-line">{{ col.headerTop }}</div>
                  <div class="hdr-line">AMT</div>
                </th>
              </template>
              <th class="text-center cell-amount">Fund Total</th>
              <th class="text-center cell-amount">Received</th>
              <th class="text-center cell-amount">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in reportItems" :key="index">
              <td class="text-left font-weight-bold cell-class-sec">
                {{ formatClassSec(item) }}
              </td>
              <td class="text-center cell-num">
                {{ formatCount(item.stdCount) }}
              </td>
              <td class="text-center cell-num">
                {{ formatCount(item.paidCount) }}
              </td>
              <td class="text-center cell-num">
                {{ formatCount(item.freeCount) }}
              </td>
              <td class="text-center cell-num">
                {{ formatCount(item.unpaidCount) }}
              </td>
              <td class="text-center cell-num">
                {{ formatPercent(item.paidPerc) }}
              </td>
              <td class="text-right cell-amount">
                {{ formatAmount(item.currentMonthPaidAmount) }}
              </td>
              <td class="text-center cell-num">
                {{ formatCount(item.currentMonthAdvanceCount) }}
              </td>
              <td class="text-right cell-amount">
                {{ formatAmount(item.currentMonthAdvanceAmount) }}
              </td>
              <!-- Dynamic Monthly Breakdown Cells -->
              <template v-for="col in distinctBreakdownColumns">
                <td class="text-center cell-num">
                  {{ getBreakdownQty(item, col) }}
                </td>
                <td class="text-right cell-amount">
                  {{ getBreakdownAmt(item, col) }}
                </td>
              </template>
              <td class="text-right cell-amount">
                {{ formatAmount(item.fundTotal) }}
              </td>
              <td class="text-right cell-amount">
                {{ formatAmount(item.totalReceived) }}
              </td>
              <td class="text-right cell-amount">
                {{ formatAmount(item.currentMonthUnpaidAmount) }}
              </td>
            </tr>

            <!-- Grand Total Row: placed at the end of tbody so it only prints once at the end and does not repeat on each page -->
            <tr v-if="grandTotal" class="grand-total-row">
              <td class="text-left font-weight-bold cell-class-sec">
                GRAND TOTAL
              </td>
              <td class="text-center cell-num font-weight-bold">
                {{ formatCount(grandTotal.stdCount) }}
              </td>
              <td class="text-center cell-num font-weight-bold">
                {{ formatCount(grandTotal.paidCount) }}
              </td>
              <td class="text-center cell-num font-weight-bold">
                {{ formatCount(grandTotal.freeCount) }}
              </td>
              <td class="text-center cell-num font-weight-bold">
                {{ formatCount(grandTotal.unpaidCount) }}
              </td>
              <td class="text-center cell-num font-weight-bold">
                {{ formatPercent(grandTotal.paidPerc) }}
              </td>
              <td class="text-right cell-amount font-weight-bold">
                {{ formatAmount(grandTotal.currentMonthPaidAmount) }}
              </td>
              <td class="text-center cell-num font-weight-bold">
                {{ formatCount(grandTotal.currentMonthAdvanceCount) }}
              </td>
              <td class="text-right cell-amount font-weight-bold">
                {{ formatAmount(grandTotal.currentMonthAdvanceAmount) }}
              </td>
              <!-- Dynamic Monthly Breakdown Grand Totals -->
              <template v-for="col in distinctBreakdownColumns">
                <td class="text-center cell-num font-weight-bold">
                  {{ getBreakdownTotalQty(col) }}
                </td>
                <td class="text-right cell-amount font-weight-bold">
                  {{ getBreakdownTotalAmt(col) }}
                </td>
              </template>
              <td class="text-right cell-amount font-weight-bold">
                {{ formatAmount(grandTotal.fundTotal) }}
              </td>
              <td class="text-right cell-amount font-weight-bold">
                {{ formatAmount(grandTotal.totalReceived) }}
              </td>
              <td class="text-right cell-amount font-weight-bold">
                {{ formatAmount(grandTotal.currentMonthUnpaidAmount) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- No Data State -->
      <div v-else class="no-data-placeholder text-center py-5">
        <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
        <p class="text-muted font-weight-bold mb-0">
          No records found for the selected criteria.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "OutstandingSummaryPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      grandTotal: null,
      monthName: "",
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: 0,
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      depIDs: "",
      dtFrom: "",
      dtTo: "",
    };
  },
  computed: {
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    monthNameDisplay() {
      if (this.monthName) return this.monthName;
      return this.getMonthName(this.month);
    },
    monthShortName() {
      const name = this.monthNameDisplay || "";
      const match = name.match(/^[A-Za-z]+/);
      return (match ? match[0] : name).slice(0, 3).toUpperCase() || "MTH";
    },
    monthShortTitle() {
      const name = this.monthNameDisplay || "";
      const match = name.match(/^[A-Za-z]+/);
      const raw = match ? match[0] : name;
      if (!raw) return "Mth";
      return (
        raw.slice(0, 3).charAt(0).toUpperCase() +
        raw.slice(1, 3).toLowerCase()
      );
    },
    currentMonthHdr() {
      let name = (this.monthNameDisplay || "").trim().toUpperCase();
      const match = name.match(/^[A-Z]+/);
      if (match) name = match[0];
      if (name.length > 5) {
        name = name.slice(0, 3);
      }
      return name || "MTH";
    },
    distinctBreakdownColumns() {
      const map = new Map();
      if (!Array.isArray(this.reportItems)) return [];

      this.reportItems.forEach((row) => {
        const breakdowns = Array.isArray(row.monthlyBreakdown)
          ? row.monthlyBreakdown
          : [];
        breakdowns.forEach((b) => {
          if (!b) return;
          const month = Number(b.month) || 0;
          const year = Number(b.year) || 0;
          const rawTag = (b.tag || "arrear").toString().trim().toLowerCase();
          const isAdv = rawTag === "advance" || rawTag.includes("adv");
          const tag = isAdv ? "advance" : "arrear";
          const key = `${year}_${month}_${tag}`;

          if (!map.has(key)) {
            let monthName = b.monthName || "";
            if (!monthName && month >= 1 && month <= 12) {
              monthName = this.getMonthName(month);
            }
            const match = monthName.match(/^[A-Za-z]+/);
            const raw = match ? match[0] : monthName;
            const shortName = raw
              ? raw.slice(0, 3).charAt(0).toUpperCase() +
                raw.slice(1, 3).toLowerCase()
              : "Mth";

            let monthText = (b.monthName || "").trim().toUpperCase();
            if (!monthText && month >= 1 && month <= 12) {
              monthText = this.getMonthName(month).toUpperCase();
            }
            monthText = monthText.slice(0, 3);
            
            const headerTop = isAdv ? `${monthText} ADV` : monthText;

            const qtyHeader = `${shortName} ${isAdv ? "Adv " : ""}Qty`;
            const amtHeader = `${shortName} ${isAdv ? "Adv " : ""}Amt`;

            map.set(key, {
              key,
              month,
              year,
              tag,
              monthName,
              shortName,
              isAdv,
              headerTop,
              qtyHeader,
              amtHeader,
            });
          }
        });
      });

      return Array.from(map.values()).sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        if (a.month !== b.month) return a.month - b.month;
        if (a.tag !== b.tag) {
          return a.tag === "arrear" ? -1 : 1;
        }
        return 0;
      });
    },
    breakdownTotals() {
      const totals = {};
      const cols = this.distinctBreakdownColumns || [];
      cols.forEach((col) => {
        let totalCount = 0;
        let totalAmount = 0;
        let hasAny = false;

        if (Array.isArray(this.reportItems)) {
          this.reportItems.forEach((row) => {
            const item = this.getBreakdownItem(row, col);
            if (item) {
              if (item.studentCount != null && !isNaN(item.studentCount)) {
                totalCount += Number(item.studentCount);
                hasAny = true;
              }
              if (item.paidAmount != null && !isNaN(item.paidAmount)) {
                totalAmount += Number(item.paidAmount);
                hasAny = true;
              }
            }
          });
        }

        totals[col.key] = {
          hasAny,
          studentCount: totalCount,
          paidAmount: totalAmount,
        };
      });
      return totals;
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query || {};
      this.db = query.db || this.$store.state.userData?.db || "";
      this.cID = query.cID !== undefined ? query.cID : (this.$store.state.userData?.cId || 0);
      this.month = query.month ? Number(query.month) : (new Date().getMonth() + 1);
      this.year = query.year ? Number(query.year) : new Date().getFullYear();
      this.depIDs = query.depIDs !== undefined ? query.depIDs : "";
      this.dtFrom = query.dtFrom || "";
      this.dtTo = query.dtTo || "";

      if (!this.db) {
        this.error = "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    formatClassSec(item) {
      if (!item) return "-";
      const cls = (item.className || "").trim();
      const sec = (item.sectionName || "").trim();
      if (cls && sec) return `${cls} ${sec}`;
      return cls || sec || "-";
    },
    getBreakdownItem(row, col) {
      if (!row || !Array.isArray(row.monthlyBreakdown)) return null;
      return row.monthlyBreakdown.find((b) => {
        if (!b) return false;
        const month = Number(b.month) || 0;
        const year = Number(b.year) || 0;
        const rawTag = (b.tag || "arrear").toString().trim().toLowerCase();
        const tag = rawTag === "advance" || rawTag.includes("adv") ? "advance" : "arrear";
        return month === col.month && year === col.year && tag === col.tag;
      });
    },
    getBreakdownQty(row, col) {
      const item = this.getBreakdownItem(row, col);
      if (!item || item.studentCount === null || item.studentCount === undefined) {
        return "-";
      }
      return this.formatCount(item.studentCount);
    },
    getBreakdownAmt(row, col) {
      const item = this.getBreakdownItem(row, col);
      if (!item || item.paidAmount === null || item.paidAmount === undefined) {
        return "-";
      }
      return this.formatAmount(item.paidAmount);
    },
    getBreakdownTotalQty(col) {
      const t = this.breakdownTotals[col.key];
      if (!t || !t.hasAny) return "-";
      return this.formatCount(t.studentCount);
    },
    getBreakdownTotalAmt(col) {
      const t = this.breakdownTotals[col.key];
      if (!t || !t.hasAny) return "-";
      return this.formatAmount(t.paidAmount);
    },
    parseDepIDs(val) {
      if (!val || val === "0" || val === 0) {
        return [];
      }
      if (Array.isArray(val)) {
        return val
          .filter((id) => id && id != 0)
          .map((id) => Number(id) || id);
      }
      return String(val)
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id && id !== "0")
        .map((id) => Number(id) || id);
    },
    getMonthName(monthNum) {
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const m = Number(monthNum);
      if (m >= 1 && m <= 12) {
        return months[m - 1];
      }
      return "";
    },
    formatCount(val) {
      if (val === null || val === undefined || isNaN(val)) return "0";
      return Number(val).toLocaleString();
    },
    formatAmount(val) {
      if (val === null || val === undefined || isNaN(val)) return "0";
      return Number(val).toLocaleString();
    },
    formatPercent(val) {
      if (val === null || val === undefined || isNaN(val)) return "0%";
      const num = Number(val);
      if (Number.isInteger(num)) {
        return `${num}%`;
      }
      return `${num.toFixed(2)}%`;
    },
    async loadCampusInfo() {
      try {
        const url = "https://apiv26.myskool.app/api/campuses";
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData?.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name || "MY SKOOL";
            this.campusBranch = this.cID == 0 ? "All Campuses" : (match.branch || "");
            this.schoolLogo = match.logo || null;
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed, using fallback from store", e);
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const baseUrl =
          this.$store.state.domain || "https://apiv26.myskool.app/api/";
        const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
        const url = `${cleanBaseUrl}Fees/OutstandingSummary?db=${encodeURIComponent(this.db)}`;

        const payload = {
          campusID: Number(this.cID) || 0,
          month: Number(this.month),
          year: Number(this.year),
          depIDs: this.parseDepIDs(this.depIDs),
        };

        const response = await axios.post(url, payload, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData?.token}`,
            accept: "application/json",
            "content-type": "application/json",
          },
        });

        if (response.data && response.data.status === "success") {
          const responseData = response.data.data || {};
          this.monthName = responseData.monthName || "";
          this.reportItems = responseData.orderedResult || [];
          this.grandTotal = responseData.grandTotal || null;
        } else {
          this.error =
            response.data?.message ||
            "The server returned an unexpected response format.";
        }
      } catch (err) {
        console.error("Error fetching Outstanding Summary report:", err);
        this.error =
          "Failed to retrieve report data. Please ensure the API endpoint is accessible.";
      } finally {
        this.loading = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.outstanding-summary-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #1b3b6f 0%, #2980b9 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #142a50 0%, #20638f 100%);
    box-shadow: 0 6px 20px rgba(27, 59, 111, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 2.5rem 2rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1400px;
  margin: 20px auto;
  box-sizing: border-box;
}

/* Header matching reference image */
.report-header-modern {
  .header-inner {
    position: relative;
    padding: 0.5rem 0;
  }

  .logo-wrapper {
    left: 0;
    top: 50%;
    transform: translateY(-50%);

    .school-logo-img {
      height: 90px;
      max-width: 180px;
      object-fit: contain;
    }
  }

  .school-name-full {
    font-size: 1.85rem;
    font-weight: 800;
    color: #1b3b6f;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .report-title-text {
    font-size: 1.05rem;
    font-weight: 700;
    color: #2c3e50;
    letter-spacing: -0.2px;
  }

  .generated-date-text {
    font-size: 0.85rem;
    color: #7f8c8d;
  }

  .header-divider-line {
    border-bottom: 2px solid #1b3b6f;
    width: 100%;
  }
}

/* Grand Total Metric Bar matching screenshot */
.summary-metric-bar {
  background: #ebf5fe;
  border: 1px solid #bce1fd;
  border-radius: 8px;
  padding: 12px 18px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-wrap: nowrap;
  gap: 12px;

  .summary-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    flex: 1 1 0px;
    min-width: 0;

    .summary-label {
      font-size: 0.72rem;
      font-weight: 700;
      color: #1976d2;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
      white-space: nowrap;
    }

    .summary-value {
      font-size: 1.15rem;
      font-weight: 800;
      color: #0d2b56;
      white-space: nowrap;
    }
  }
}

/* Details Data Table */
.table-container {
  width: 100%;
  overflow-x: auto;
}

.summary-data-table {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  font-size: 0.85rem;

  th,
  td {
    border: 1px solid #d2d6dc;
    padding: 7px 9px;
    box-sizing: border-box;
    font-size: 0.84rem;
  }

  thead {
    background: #1A365D !important;

    th {
      background: #1A365D !important;
      color: #ffffff !important;
      font-weight: 700;
      text-transform: uppercase;
      font-size: 0.76rem;
      letter-spacing: 0.4px;
      vertical-align: middle;
      border: 1px solid #23477d;
      white-space: nowrap;
    }
  }

  tbody {
    tr:nth-child(even):not(.grand-total-row) {
      background-color: #f8fafc;
    }

    tr:hover:not(.grand-total-row) {
      background-color: #f1f5f9;
    }

    td {
      color: #1e293b;
    }
  }

  .grand-total-row {
    background: #edf2f7;

    td {
      border-top: 2px solid #1A365D !important;
      border-bottom: 2px solid #1A365D !important;
      color: #0f172a !important;
      font-size: 0.86rem !important;
      font-weight: 800 !important;
    }
  }

  .cell-class-sec {
    min-width: 110px;
  }

  .cell-num {
    min-width: 48px;
    white-space: nowrap;
  }

  .cell-amount {
    min-width: 65px;
    white-space: nowrap;
  }

  .cell-stacked-hdr {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
    line-height: 1.15;

    .hdr-line {
      display: block;
      white-space: nowrap;
    }
  }
}

.no-data-placeholder {
  background: #fafbfc;
  border: 1px dashed #d1d8e0;
  border-radius: 8px;
  margin-top: 1.5rem;
}

@page {
  size: landscape;
  margin: 3mm;
}

@media print {
  @page {
    size: landscape;
    margin: 3mm;
  }

  .no-print {
    display: none !important;
  }

  html,
  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
    width: 100% !important;
  }

  .outstanding-summary-report {
    background: white !important;
    padding: 0 !important;
    margin: 0 !important;
    min-height: auto !important;
    width: 100% !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .report-header-modern {
    margin-bottom: 8px !important;

    .school-name-full {
      font-size: 1.4rem !important;
      color: #1b3b6f !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .report-title-text {
      font-size: 0.88rem !important;
      color: #2c3e50 !important;
    }

    .header-divider-line {
      border-bottom: 2px solid #1b3b6f !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .logo-wrapper .school-logo-img {
      height: 55px !important;
    }
  }

  .summary-metric-bar {
    background: #ebf5fe !important;
    border: 1px solid #bce1fd !important;
    padding: 5px 8px !important;
    margin-bottom: 10px !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    .summary-col {
      .summary-label {
        font-size: 0.6rem !important;
        color: #1976d2 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .summary-value {
        font-size: 0.9rem !important;
        color: #0d2b56 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  }

  .table-container {
    width: 100% !important;
    overflow: visible !important;
    display: block !important;
    zoom: 0.91 !important;
  }

  .summary-data-table {
    width: 100% !important;
    max-width: 100% !important;
    table-layout: auto !important;
    font-size: 5.8pt !important;
    border-collapse: collapse !important;

    th,
    td {
      padding: 1.5px 1.5px !important;
      border: 1px solid #475569 !important;
      font-size: 5.8pt !important;
      line-height: 1.1 !important;
    }

    tr {
      page-break-inside: avoid !important;
    }

    thead th {
      background: #1A365D !important;
      color: #ffffff !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      font-size: 5.8pt !important;
      padding: 2.5px 1px !important;
      border: 1px solid #1A365D !important;
      letter-spacing: -0.2px !important;
    }

    tbody td {
      padding: 4px 1.5px !important;
      line-height: 1.2 !important;
    }

    .cell-class-sec {
      min-width: 0 !important;
      max-width: 55px !important;
      white-space: normal !important;
      word-break: break-word !important;
      padding: 4px 2px !important;
      line-height: 1.2 !important;
    }

    .cell-num {
      min-width: 0 !important;
      white-space: nowrap !important;
      padding: 4px 1px !important;
    }

    .cell-amount {
      min-width: 0 !important;
      white-space: nowrap !important;
      padding: 4px 1px !important;
    }

    .cell-stacked-hdr {
      padding-top: 1.5px !important;
      padding-bottom: 1.5px !important;
      line-height: 1.05 !important;
      min-width: 0 !important;

      .hdr-line {
        display: block !important;
        white-space: nowrap !important;
      }
    }

    tbody tr:nth-child(even):not(.grand-total-row) {
      background-color: #f8fafc !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    .grand-total-row {
      background: #e2e8f0 !important;
      page-break-inside: avoid !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;

      td {
        font-weight: 800 !important;
        font-size: 5.8pt !important;
        padding: 4px 1px !important;
        border-top: 2px solid #1A365D !important;
        border-bottom: 2px solid #1A365D !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  }
}
</style>
